import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Typography, Link, Divider, TextField } from '@mui/material';
import Box from "@mui/material/Box";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { ReactComponent as GoogleIcon } from '../images/google-icon.svg';
import logo from '../images/logo.svg';

import { auth, provider, db } from '../firebase';
import {
    signInWithPopup,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
} from 'firebase/auth';
import { setDoc, doc } from 'firebase/firestore';

/** メールリンクのActionCodeSettings
 *  着地先はこの同じ画面（/）。shop.conekuta.com に固定。
 *  ※Authorized domains に shop.conekuta.com が登録されていればOK
 */
const actionCodeSettings = {
  url: `${window.location.origin}/`,
  handleCodeInApp: true,
  // Webのみ運用なので Dynamic Links は不要
};

export default function SignUp() {
  const [email, setEmail] = useState(() => localStorage.getItem('emailForSignIn') || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [needEmail, setNeedEmail] = useState(false);
  const completeOnceRef = useRef(false);

  // Googleサインイン（既存）
  const signInWithGoogle = async () => {
    // signInWithPopup(auth, provider);
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Firestoreにユーザー情報を保存
      const userRef = doc(db, 'customers', user.uid);
      await setDoc(userRef, {
        displayName: user.displayName || '',
        email: user.email,
        // 他に保存したいユーザー情報があればここに追加
      }, { merge: true });
    } catch (error) {
      // サインインに失敗した場合のエラーハンドリング
      console.error('サインインエラー', error);
    }
  };

  // メールリンク送信
  const sendLink = async () => {
    if (!email) return;
    try {
      setSending(true);
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      localStorage.setItem('emailForSignIn', email);
      setSent(true);
    } catch (e) {
      console.error(e);
      // アカウント列挙防止のため固定文言でOK
      alert('メールを送信しました。届かない場合は数分後に再度お試しください。');
    } finally {
      setSending(false);
    }
  };

  // メールリンクの着地を同一画面で処理
  useEffect(() => {
    if (completeOnceRef.current) return;

    if (isSignInWithEmailLink(auth, window.location.href)) {
      if (!email) {
        // 別端末/別ブラウザで開いた場合はメール再入力を促す
        setNeedEmail(true);
        return;
      }
      const complete = async () => {
        try {
          completeOnceRef.current = true;
          setCompleting(true);
          const result = await signInWithEmailLink(auth, email, window.location.href);
          localStorage.removeItem('emailForSignIn');

          // Firestoreにユーザー情報を保存（Googleログインと同等）
          const u = result.user;
          const userRef = doc(db, 'customers', u.uid);
          await setDoc(userRef, {
            displayName: u.displayName || '',
            email: u.email || email,
          }, { merge: true });

          // 完了後はトップへ
          window.location.replace('/');
        } catch (e) {
          console.error(e);
          alert('リンクが無効か期限切れの可能性があります。もう一度お試しください。');
        } finally {
          setCompleting(false);
        }
      };
      complete();
    }
  }, [email]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', px: 0, py: 0 }}>
      {/* ヘッダー部分 */}
      <Box sx={{ backgroundColor:'#f9db00', textAlign: 'center', mb: 2, py: 2 }}>
        <img src={logo} alt="Conekuta" style={{ height: '1.25rem' }} />
      </Box>

      <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          ログイン
        </Typography>

        <Divider sx={{ mt: 2, mb: 1, borderColor: '#e0e0e0' }} />

        {/* メールリンク */}
        <Typography variant="body2" fontWeight="bold" sx={{ my: 1 }}>
          メールアドレスでログイン
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', my: 2, fontSize: '0.775rem' }}>
          入力したアドレスに、本人確認メールが送信されます。<br/>
        </Typography>

        <Box sx={{ px: 3, pb: 5 }}>
            {/* 注意メッセージ */}
            {needEmail && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        my: 2,
                        p: 1.5,
                        backgroundColor: '#ffebee', // 薄い赤
                        border: '1px solid #ef9a9a', // 赤系の枠
                        borderRadius: '6px',
                        color: '#b71c1c', // 濃い赤
                        fontWeight: 'bold',
                    }}
                >
                    他の端末やブラウザで開いた場合、<br />
                    本人確認ができないことがあります。<br />
                    再度、本人確認メールを送信してください。
                </Typography>
            )}
            {/* 送信完了メッセージ */}
            {sent && !isSignInWithEmailLink(auth, window.location.href) && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        my: 2,
                        p: 1.5,
                        backgroundColor: '#e3f2fd', // 薄い青
                        border: '1px solid #90caf9', // 青系の枠
                        borderRadius: '6px',
                        color: '#0d47a1', // 濃い青
                        fontWeight: 'bold', // 太字
                    }}
                >
                    メールを送信しました。<br />
                    届いたリンクをタップして認証を完了してください。<br />
                    ※迷惑メールフォルダに届く場合がございます。
                </Typography>
            )}
            {/* 本人確認メール送信フォーム */}
            <TextField
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoComplete="email"
                size="small"
                fullWidth
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 1 }}
            />
            {!isSignInWithEmailLink(auth, window.location.href) ? (
                <Button
                    onClick={sendLink}
                    disabled={sending || !email}
                    variant="contained"
                    sx={{ width: '100%', fontWeight: 'bold' }}
                >
                    {sending ? '送信中…' : '本人確認メールを送信する　　　'}
                    <ArrowForwardIosIcon fontSize="small"/>
                </Button>
            ) : (
                <Button
                    disabled
                    variant="contained"
                    sx={{ width: '100%', fontWeight: 'bold' }}
                >
                    {completing ? 'ログイン処理中…' : '処理待機中…'}
                </Button>
            )}
        </Box>

        <Divider sx={{ mt: 2, mb: 1, borderColor: '#e0e0e0' }} />

        {/* Google */}
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          外部サービスのアカウントでログイン
        </Typography>
        <Button
          variant="outlined"
          startIcon={<GoogleIcon style={{ width: '20px', height: '20px' }} />}
          onClick={signInWithGoogle}
          sx={{
            width: '80%',
            backgroundColor: '#ffffff',
            color: '#131314',
            borderColor: '#8E918F',
            borderWidth: '1px',
            borderRadius: '20px',
            fontSize: '14px',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
            textTransform: 'none',
            height: 40,
            '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#8E918F' },
            mb: 4,
          }}
        >
          Google
        </Button>

        <Divider sx={{ mt: 3, mb: 1, borderColor: '#e0e0e0' }} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          まだ店舗アカウントをお持ちでない方は{' '}
          <Link
            component={RouterLink}
            to="/"
            underline="hover"
            sx={{ cursor: 'pointer' }}
          >
            こちら
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
